import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AuthProvider, ProtectedRoute } from './auth/index.js';
import { GoogleCallback } from './pages/google-callback.js';
import { SlackCallback } from './pages/slack-callback.js';
import { Privacy, Terms } from './pages/legal.js';
import { NotificationsProvider } from './hooks/use-notifications.js';
import { ToastProvider } from './hooks/use-toast.js';
import { Layout } from './components/layout.js';
import { Todo } from './pages/todo.js';
import { Servers } from './pages/servers.js';
import { ServerDetail } from './pages/server-detail.js';
import { Sessions } from './pages/sessions.js';
import { SessionDetail } from './pages/session-detail.js';
import { Costs } from './pages/costs.js';
import { Schedules } from './pages/schedules.js';
import { Settings } from './pages/settings.js';
import { Login } from './pages/login.js';
import { NotFound } from './pages/not-found.js';
import { PersonSheet } from './pages/person-sheet.js';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ToastProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route element={<ProtectedRoute />}>
              {/* A page to print, without the app around it */}
              <Route path="/servers/:serverId/people/:personId/sheet" element={<PersonSheet />} />
              <Route
                element={
                  <NotificationsProvider>
                    <Layout />
                  </NotificationsProvider>
                }
              >
                <Route path="/" element={<Todo />} />
                <Route path="/servers" element={<Servers />} />
                <Route path="/servers/:serverId" element={<ServerDetail />} />
                <Route path="/servers/:serverId/reviews" element={<ServerDetail tab="reviews" />} />
                <Route path="/servers/:serverId/people" element={<ServerDetail tab="people" />} />
                <Route path="/servers/:serverId/sprint" element={<ServerDetail tab="sprint" />} />
                <Route path="/servers/:serverId/mail" element={<ServerDetail tab="mail" />} />
                <Route path="/servers/:serverId/meetings" element={<ServerDetail tab="meetings" />} />
                <Route path="/servers/:serverId/settings" element={<ServerDetail tab="settings" />} />
                <Route path="/servers/:serverId/files" element={<ServerDetail tab="files" />} />
                <Route path="/servers/:serverId/tickets/:ref" element={<ServerDetail tab="ticket" />} />
                <Route path="/servers/:serverId/slack/:group" element={<ServerDetail tab="slack" />} />
                <Route path="/servers/:serverId/sessions" element={<Sessions />} />
                <Route path="/servers/:serverId/sessions/:sessionId" element={<SessionDetail />} />
                <Route path="/schedules" element={<Schedules />} />
                <Route path="/costs" element={<Costs />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/google/callback" element={<GoogleCallback />} />
                <Route path="/slack/callback" element={<SlackCallback />} />
              </Route>
            </Route>
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
        </ToastProvider>
      </AuthProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}
